import { useState, useCallback, useEffect } from 'react';
import shallow from 'zustand/shallow';
import { giftCardApi } from '@api';
import { useGiftCardState } from '@root/state';
import { useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { IGiftCardExtendPackage, IGiftCardPackage } from '@root/types/package';
import useCreateOrder from './useCreateOrder';
import useBillAddress from '../useBillAddress';
import useUnBindDevices from '../useUnBindDevices';

const usePackages = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const navigateType = useNavigationType();
  const { isBillAddress, onAddBill } = useBillAddress();
  const { list: unBindDevices } = useUnBindDevices();
  const [loading, setLoading] = useState(0); // 0, 1, 2 loading， success，fail
  const {
    selectedPackage,
    selectExtendPackage,
    setSelectExtendPackages,
    setSelectedPackage,
    resetInitData,
  } = useGiftCardState(
    (state) => ({
      selectExtendPackage: state.selectExtendPackage,
      selectedPackage: state.selectedPackage,
      setSelectExtendPackages: state.setSelectExtendPackages,
      setSelectedPackage: state.setSelectedPackage,
      resetInitData: state.resetInitData,
    }),
    shallow,
  );
  const [packages, setPackages] = useState<IGiftCardPackage[]>([]);
  const [extendPackages, setExtendPackages] = useState<
    IGiftCardExtendPackage[]
  >([]);
  const { paying, onCreateOrder, onCreateGiftCardExtensionOrder } =
    useCreateOrder();

  const onSelected = useCallback(
    (item: IGiftCardPackage) => {
      setSelectedPackage(item);
      setSelectExtendPackages(null);
    },
    [setSelectExtendPackages, setSelectedPackage],
  );

  const onSelectedExtend = useCallback(
    (item: IGiftCardExtendPackage) => {
      setSelectedPackage(null);
      setSelectExtendPackages(item);
    },
    [setSelectExtendPackages, setSelectedPackage],
  );

  const onClickPay = useCallback(() => {
    // 礼品卡延期已有服务
    if (selectExtendPackage) {
      if (selectExtendPackage.payMark) {
        navigate(
          `/giftCard/${code}/packages/${selectExtendPackage.packageId}/payMethod`,
        );
      } else {
        onCreateGiftCardExtensionOrder(selectExtendPackage);
      }
      return;
    }

    // 礼品卡兑换服务
    if (!selectedPackage) {
      return;
    }

    if (!isBillAddress && selectedPackage.payMark) {
      onAddBill();
      return;
    }

    // 有未绑定的设备，或需要支付的礼品卡默认跳转选择设备页面（要选择支付方式）
    if (unBindDevices.length || selectedPackage.payMark) {
      navigate(
        `/giftCard/${code}/packages/${selectedPackage.goodsId}/selectDevice`,
      );
      return;
    }

    onCreateOrder({ curPackage: selectedPackage });
  }, [
    selectExtendPackage,
    selectedPackage,
    isBillAddress,
    unBindDevices.length,
    onCreateOrder,
    navigate,
    code,
    onCreateGiftCardExtensionOrder,
    onAddBill,
  ]);

  const getPackages = useCallback(() => {
    if (!code) {
      return;
    }

    giftCardApi
      .getPackagesByCode(code)
      .then((data: any) => {
        setPackages(data || []);
        setLoading(1);
      })
      .catch(() => {
        setLoading(2);
      });

    giftCardApi
      .getGiftCardExtensionGoods(code)
      .then((data: any) => {
        setExtendPackages(data || []);
      })
      .catch(() => {
        // catch
      });
  }, [code]);

  useEffect(() => {
    getPackages();
  }, [getPackages]);

  useEffect(() => {
    if (navigateType === 'PUSH') {
      resetInitData();
    }
  }, [navigateType, resetInitData]);

  return {
    state: {
      paying,
      packages,
      extendPackages,
      loading,
      unBindDevices,
      selectedPackage,
      selectExtendPackage,
    },
    action: {
      setSelectedPackage,
      getPackages,
      resetInitData,
      onClickPay,
      onSelected,
      onSelectedExtend,
    },
  };
};

export default usePackages;
